import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { eur, shortDate } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Briefcase, Store, Users, CheckCircle2, Clock, Archive, ChevronRight, Wallet, ListChecks, FileText } from "lucide-react";

const STATUS_TABS = ["active", "archived"];

function ProjectDetail({ project, onClose }) {
  const t = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/coach/collab/projects/${project.type}/${project.id}/detail`)
      .then(({ data: d }) => setData(d)).catch(() => setData({}));
  }, [project.type, project.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="collab-project-detail">
        <DialogHeader>
          <DialogTitle>{project.title}</DialogTitle>
          <DialogDescription>{t(`col.project_type_${project.type}`)}</DialogDescription>
        </DialogHeader>
        {data === null ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-3 text-center" data-testid="collab-project-revenue">
                <p className="font-display text-xl font-semibold text-foreground">{eur(data.revenue_all_cents || 0)}</p>
                <p className="text-[11px] text-muted-foreground">{t("col.project_revenue")}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 text-center" data-testid="collab-project-myshare">
                <p className="font-display text-xl font-semibold text-primary">{eur(data.my_share_cents || 0)}</p>
                <p className="text-[11px] text-muted-foreground">{t("col.project_my_share", { pct: data.my_split_percent || 0 })}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-3 text-center" data-testid="collab-project-participants">
                <p className="font-display text-xl font-semibold text-foreground">{(data.participants || []).length}</p>
                <p className="text-[11px] text-muted-foreground">{t("col.project_participants")}</p>
              </div>
            </div>

            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Users className="h-3.5 w-3.5" /> {t("col.project_team")}</p>
              <div className="flex flex-wrap gap-2">
                {(data.coaches || []).map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground">
                    {c.photo ? <img src={c.photo} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">{(c.name || "?").charAt(0)}</span>}
                    {c.name}{data.splits?.[c.id] != null ? ` · ${data.splits[c.id]}%` : ""}
                  </span>
                ))}
              </div>
            </div>

            {(data.tasks || []).length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ListChecks className="h-3.5 w-3.5" /> {t("col.project_tasks")}</p>
                <div className="space-y-1.5">
                  {data.tasks.map((tk, i) => (
                    <div key={tk.id || i} className="flex items-center gap-2 rounded-xl border border-border bg-card p-2.5">
                      {tk.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />}
                      <span className={`min-w-0 flex-1 truncate text-sm ${tk.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{tk.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(data.documents || []).length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><FileText className="h-3.5 w-3.5" /> {t("col.project_documents")}</p>
                <div className="space-y-1.5">
                  {data.documents.map((d, i) => (
                    <a key={d.id || i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-border bg-card p-2.5 text-sm text-primary hover:bg-accent">
                      <FileText className="h-4 w-4 shrink-0" /> <span className="min-w-0 flex-1 truncate">{d.title || d.url}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {(data.purchases || []).length > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> {t("col.project_purchases")}</p>
                <div className="space-y-1.5">
                  {data.purchases.slice(0, 20).map((p, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-border bg-card p-2.5 text-sm">
                      <span className="text-muted-foreground">{p.created_at ? shortDate(p.created_at) : "—"}</span>
                      <span className="font-medium text-foreground">{eur(p.amount_cents || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsTab() {
  const t = useT();
  const [status, setStatus] = useState("active");
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try { const { data } = await api.get(`/coach/collab/projects?status=${status}`); setRows(data?.projects || []); }
    catch { setRows([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="collab-projects-tab">
      <div className="mb-4 flex gap-1.5">
        {STATUS_TABS.map((tab) => (
          <button key={tab} onClick={() => setStatus(tab)} data-testid={`collab-projects-status-${tab}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${status === tab ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {tab === "active" ? <Clock className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            {t(`col.project_status_${tab}`)}
          </button>
        ))}
      </div>

      {rows === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="collab-projects-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Briefcase className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t(`col.project_empty_${status}`)}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("col.project_empty_hint")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => {
            const frac = p.task_count > 0 ? p.task_done / p.task_count : 0;
            return (
              <button key={`${p.type}-${p.id}`} onClick={() => setDetail(p)} data-testid={`collab-project-${p.type}-${p.id}`}
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent/40">
                {p.cover_image_url ? (
                  <img src={p.cover_image_url} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                ) : (
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${p.type === "circle" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>
                    {p.type === "circle" ? <Users className="h-6 w-6" /> : <Store className="h-6 w-6" />}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-foreground">{p.title}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${p.type === "circle" ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}>{t(`col.project_type_${p.type}`)}</span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {eur(p.price_cents || 0)}
                    {p.member_count != null ? ` · ${t("col.project_members_n", { n: p.member_count })}` : ""}
                    {p.task_count > 0 ? ` · ${p.task_done}/${p.task_count} ${t("col.project_tasks")}` : ""}
                  </p>
                  {p.task_count > 0 && (
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-border">
                      <div className="h-1 rounded-full bg-primary" style={{ width: `${Math.round(frac * 100)}%` }} />
                    </div>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
